// =============================================================================
// MeuDriver — Landing Featured Instructors + Specialties (Sections 4 + 5)
// =============================================================================

import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Users,
  Car,
  Shield,
  TrendingUp,
  RefreshCw,
  ParkingSquare,
  Route,
  Play,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InstructorCard } from '@/components/instructor/InstructorCard'
import { useInstructors } from '@/hooks/useInstructors'
import { SPECIALTY_LABELS } from '@/lib/constants'
import type { InstructorWithProfile, SpecialtyType } from '@/types/database'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay } }),
}
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }
const viewportOnce = { once: true, margin: '-80px' }

const SPECIALTY_ICONS: Record<SpecialtyType, React.ReactNode> = {
  iniciantes:         <Users className="h-5 w-5" />,
  cnh_b:              <Car className="h-5 w-5" />,
  cnh_a:              <Play className="h-5 w-5" />,
  direcao_defensiva:  <Shield className="h-5 w-5" />,
  cambio_manual:      <TrendingUp className="h-5 w-5" />,
  cambio_automatico:  <RefreshCw className="h-5 w-5" />,
  estacionamento:     <ParkingSquare className="h-5 w-5" />,
  rodovia:            <Route className="h-5 w-5" />,
}

// Seleciona até 6 featured; se menos de 3, completa com top-rated.
function selectFeatured(instructors: InstructorWithProfile[]): InstructorWithProfile[] {
  const featured = instructors.filter((i) => (i as InstructorWithProfile & { is_featured?: boolean }).is_featured)
  if (featured.length >= 3) return featured.slice(0, 6)
  // Complementa com top-rated (que não estejam já na lista featured)
  const featuredIds = new Set(featured.map((i) => i.id))
  const topRated = instructors
    .filter((i) => !featuredIds.has(i.id))
    .sort((a, b) => (b.rating_avg ?? 0) - (a.rating_avg ?? 0))
  return [...featured, ...topRated].slice(0, 6)
}

export function LandingFeaturedInstructors() {
  const { instructors, isLoading } = useInstructors()
  const display = selectFeatured(instructors)

  return (
    <section className="bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }} className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between mb-10">
          <div>
            <Badge className="mb-3 text-sm px-3 py-1 bg-brand-50 text-brand-700 border-brand-100">Mais bem avaliados</Badge>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl tracking-tight">Instrutores em destaque</h2>
            <p className="mt-2 text-gray-500">Conheça alguns dos instrutores mais elogiados da plataforma.</p>
          </div>
          <Button asChild variant="outline" className="shrink-0 border-brand-200 text-brand-700 hover:bg-brand-50 hover:border-brand-300">
            <Link to="/instrutores">
              Ver todos
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }, (_, i) => (
                <motion.div key={i} variants={fadeUp} custom={i * 0.1}>
                  <div className="h-72 rounded-2xl bg-gray-100 animate-pulse" />
                </motion.div>
              ))
            : display.slice(0, 3).map((instructor, i) => (
                <motion.div key={instructor.id} variants={fadeUp} custom={i * 0.1}>
                  <InstructorCard instructor={instructor} />
                </motion.div>
              ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5, delay: 0.3 }} className="mt-10 text-center">
          <Button asChild size="lg" variant="orange" className="px-10 shadow-orange/30 font-semibold">
            <Link to="/instrutores">
              Ver todos os instrutores
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  )
}

export function LandingSpecialties() {
  return (
    <section className="bg-gradient-to-b from-slate-50/70 to-white py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }} className="text-center mb-12">
          <Badge variant="orange" className="mb-3 text-sm px-3 py-1">Especialidades</Badge>
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl tracking-tight">Para todas as necessidades</h2>
          <p className="mt-3 text-gray-500 max-w-lg mx-auto">
            Desde o primeiro contato com o volante até a reciclagem de habilitados experientes.
          </p>
        </motion.div>

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {(Object.keys(SPECIALTY_LABELS) as SpecialtyType[]).map((key, i) => (
            <motion.div key={key} variants={fadeUp} custom={i * 0.05}>
              <Link
                to={`/instrutores?specialty=${key}`}
                className="group flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-white p-5 text-center shadow-soft hover:border-brand-200 hover:shadow-card hover:bg-brand-50/30 transition-all duration-200"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-500 group-hover:bg-brand-600 group-hover:text-white transition-all duration-200">
                  {SPECIALTY_ICONS[key]}
                </span>
                <span className="text-sm font-medium text-gray-700 group-hover:text-brand-700 transition-colors leading-tight">
                  {SPECIALTY_LABELS[key]}
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
